<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
import { computed, onMounted, reactive, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const DEFAULT_CONFIG = {
  enabled: false,
  threshold: 7,
  dedicated_agent_id: '',
};

const store = useStore();
const { currentAccount } = useAccount();
const form = reactive({ ...DEFAULT_CONFIG });

const agentList = computed(() => store.getters['agents/getAgents']);
const isUpdating = computed(
  () => store.getters['accounts/getUIFlags']?.isUpdating
);
const isFetchingAgents = computed(
  () => store.getters['agents/getUIFlags']?.isFetching
);

const confirmedAgents = computed(() =>
  agentList.value.filter(agent => agent.confirmed)
);

const selectedAgent = computed(() =>
  confirmedAgents.value.find(
    agent => agent.id === Number(form.dedicated_agent_id)
  )
);

const previewText = computed(() => {
  if (!form.enabled) return '分线未启用，系统继续使用原有自动分配规则。';

  return `vip=${form.threshold} 及以上玩家只分配给 ${
    selectedAgent.value?.name || '未选择客服'
  }，其余玩家在剩余客服中轮询分配。`;
});

const normalizeConfig = config => ({
  enabled: Boolean(config?.enabled),
  threshold: Number(config?.threshold || DEFAULT_CONFIG.threshold),
  dedicated_agent_id: config?.dedicated_agent_id || '',
});

const applyAccountConfig = () => {
  Object.assign(
    form,
    normalizeConfig(currentAccount.value?.settings?.vip_assignment_rules)
  );
};

const saveConfig = async () => {
  const threshold = Math.min(Math.max(Number(form.threshold) || 7, 1), 10);

  try {
    await store.dispatch('accounts/update', {
      vip_assignment_rules: {
        enabled: form.enabled,
        threshold,
        dedicated_agent_id: form.dedicated_agent_id
          ? Number(form.dedicated_agent_id)
          : '',
      },
    });
    form.threshold = threshold;
    useAlert('VIP分线规则已保存');
  } catch (error) {
    useAlert('保存失败，请稍后重试');
  }
};

watch(
  () => currentAccount.value?.settings?.vip_assignment_rules,
  applyAccountConfig,
  { immediate: true }
);

onMounted(() => {
  store.dispatch('agents/get');
});
</script>

<template>
  <SettingsLayout :is-loading="isFetchingAgents" :no-records-found="false">
    <template #header>
      <BaseSettingsHeader
        title="VIP分线（vip）"
        description="读取 App 传入的 vip 参数，配置高等级玩家进入专属客服线，其他玩家自动排除该客服后继续按原有轮询规则分配。此规则对当前账号下所有收件箱生效。"
      />
    </template>

    <template #body>
      <div class="max-w-6xl">
        <div
          class="mb-5 flex items-center justify-between rounded-xl border border-n-weak bg-n-solid-1 px-5 py-4 shadow-sm"
        >
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <span
                class="inline-flex size-9 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand"
              >
                <Icon icon="i-lucide-route" class="size-4" />
              </span>
              <div>
                <h2 class="m-0 text-base font-semibold text-n-slate-12">
                  VIP专属分配规则
                </h2>
                <p class="m-0 mt-0.5 text-sm text-n-slate-11">
                  高等级玩家进入专属客服线，普通玩家继续走轮询。
                </p>
              </div>
            </div>
          </div>
          <label
            class="relative inline-flex cursor-pointer items-center gap-3 rounded-full border border-n-weak bg-n-background py-2 pl-4 pr-2 text-sm font-medium text-n-slate-12"
          >
            <span>{{ form.enabled ? '已启用' : '未启用' }}</span>
            <input
              v-model="form.enabled"
              class="peer sr-only"
              type="checkbox"
            />
            <span
              class="h-6 w-11 rounded-full bg-n-slate-6 transition peer-checked:bg-n-brand"
            />
            <span
              class="absolute right-7 size-5 rounded-full bg-white shadow transition peer-checked:translate-x-5"
            />
          </label>
        </div>

        <div class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <section
            class="overflow-hidden rounded-xl border border-n-weak bg-n-background shadow-sm"
          >
            <div class="border-b border-n-weak bg-n-solid-1 px-5 py-4">
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                分配条件
              </h3>
              <p class="m-0 mt-1 text-sm leading-6 text-n-slate-11">
                启用后，VIP
                参数达到阈值的玩家只会分配给专属客服；普通玩家不会再分配给这名专属客服。
              </p>
            </div>

            <div class="p-5">
              <div class="grid gap-4 md:grid-cols-2">
                <label class="grid gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    vip阈值
                  </span>
                  <select
                    v-model.number="form.threshold"
                    class="h-11 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none transition hover:border-n-strong focus:border-n-brand"
                  >
                    <option v-for="level in 10" :key="level" :value="level">
                      vip {{ level }} 及以上
                    </option>
                  </select>
                </label>

                <label class="grid gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    专属客服
                  </span>
                  <select
                    v-model="form.dedicated_agent_id"
                    class="h-11 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none transition hover:border-n-strong focus:border-n-brand"
                  >
                    <option value="">请选择客服</option>
                    <option
                      v-for="agent in confirmedAgents"
                      :key="agent.id"
                      :value="agent.id"
                    >
                      {{ agent.name }} · {{ agent.email }}
                    </option>
                  </select>
                </label>
              </div>

              <div
                class="mt-5 rounded-xl border border-n-weak bg-n-solid-1 p-4"
              >
                <div class="mb-2 flex items-center gap-2">
                  <span
                    class="inline-flex size-7 items-center justify-center rounded-md bg-n-brand/10 text-n-brand"
                  >
                    <Icon icon="i-lucide-info" class="size-3.5" />
                  </span>
                  <p class="m-0 text-sm font-semibold text-n-slate-12">
                    当前规则预览
                  </p>
                </div>
                <p class="m-0 text-sm leading-6 text-n-slate-11">
                  {{ previewText }}
                </p>
              </div>

              <div
                class="mt-5 flex items-center justify-end gap-3 border-t border-n-weak pt-4"
              >
                <button
                  class="h-9 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 transition hover:bg-n-alpha-black2"
                  type="button"
                  @click="applyAccountConfig"
                >
                  取消修改
                </button>
                <button
                  class="h-9 rounded-lg bg-n-brand px-4 text-sm font-medium text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
                  type="button"
                  :disabled="isUpdating"
                  @click="saveConfig"
                >
                  {{ isUpdating ? '保存中...' : '保存配置' }}
                </button>
              </div>
            </div>
          </section>

          <aside class="rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <div class="flex items-center gap-2">
              <span
                class="inline-flex size-8 items-center justify-center rounded-lg bg-n-alpha-black2 text-n-slate-11"
              >
                <Icon icon="i-lucide-book-open" class="size-4" />
              </span>
              <h3 class="m-0 text-base font-semibold text-n-slate-12">
                使用说明
              </h3>
            </div>
            <div class="mt-4 grid gap-3 text-sm leading-6 text-n-slate-11">
              <div class="rounded-lg bg-n-background p-3">
                专属客服必须在线，且需要是对应收件箱的成员，否则 vip
                会话会保持未分配。
              </div>
              <div class="rounded-lg bg-n-background p-3">
                普通玩家会自动避开专属客服，继续在其他在线客服中按原系统轮询分配。
              </div>
              <div class="rounded-lg bg-n-background p-3">
                如果没有开启自动分配，或所有可用客服都离线，此规则不会主动分配会话。
              </div>
            </div>
          </aside>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
